// @ts-strict-ignore
import React, { MouseEvent, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import queryString from 'query-string'
import { useForm as useCryptoForm, useWatch as useCryptoWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  Box,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  Pagination,
  toast,
  useSidePanel,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { DurationInMilliseconds } from 'constants/General'
import { useThemeContext } from 'hooks/context/useThemeContext'
import { useDeleteCryptoRecordMutation } from 'hooks/mutations/useDeleteCryptoRecordMutation'
import { useDeletePreProcessedRecordMutation } from 'hooks/mutations/useDeletePreProcssedRecordMutation'
import { usePutUpdateMassCryptoPayoutMutation } from 'hooks/mutations/usePutUpdateMassCryptoPayoutMutation'
import { useGetCryptoMassPayoutById } from 'hooks/queries/MassPayouts/useGetCryptoMassPayoutById'
import { useGetMassFiatPayoutsDetailsQuery } from 'hooks/queries/MassPayouts/useGetMassFiatPayoutsDetails'
import { useGetMassFiatPayoutsRecordsQuery } from 'hooks/queries/MassPayouts/useGetMassFiatPayoutsRecords'
import { useCryptoCurrenciesQuery } from 'hooks/queries/useCryptoCurrenciesQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { useSimplePagination } from 'hooks/useSimplePagination'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { PaymentStatus } from 'types/payments'
import { WalletTypes } from 'types/types'
import { roundNumberWithCommas } from 'utils/utils'
import { WalletSheet } from '../../../pages/Wallets/components/Sheet/WalletSheet'
import { BaseErrorResponse } from '../../../types/beneficiaries'
import { ContactInstrumentsSheet } from '../../Contacts/ContactInstruments'
import {
  fiatBatchInformationAtom,
  preProcessedRecordToUpdateAtom,
  totalPaymentsAtom,
  walletTypeAtom,
} from '../atoms/massPayoutAtoms'
import { CryptoPayoutEditRecordForm } from '../components/Forms/CryptoPayoutEditRecordForm'
import { FiatPayoutEditRecordForm } from '../components/Forms/FiatPayoutEditRecordForm'
import { MassPayoutEditRecordContainer } from '../components/Forms/MassPayoutEditRecordContainer'
import { MassPayoutBatchInformation } from '../components/MassPayoutBatchInformation'
import MassPayoutHeader from '../components/MassPayoutHeader'
import MassPayoutModal from '../components/MassPayoutModal'
import { MassPayoutReviewCallouts } from '../components/MassPayoutReviewCallouts'
import { MassPayoutSidePanel } from '../components/MassPayoutSidePanel'
import { getCryptoBatchTableData } from '../components/TableData/cryptoTableData'
import { getFiatBatchTableData } from '../components/TableData/fiatTableData'
import { MER_CONTACTS_ERROR_CODES } from '../constants'
import { useTriggerContactSearch } from '../hooks/useTriggerContactSearch'
import { MassPayoutsSheets } from '../MassPayoutsSheets'
import { updateCryptoPayoutSchema } from '../schemas/updateCryptoPayoutSchema'
import {
  BatchStatus,
  BatchSummaryInformation,
  CryptoPayout,
  MassPayoutButtonGroup,
  PreProcessedPayouts,
  UpdateCryptoPayoutFormPayload,
  UpdateCryptoPayoutFormPayloadKeys,
} from '../types'
import {
  getSumOfFiatRecordsWithErrors,
  getSumOfRecords,
  getSumOfRecordsWithErrors,
  getValidRecords,
} from '../utils'

export const MassPayoutReview = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { darkMode } = useThemeContext()
  const { reference: batchId } = useParams()
  const setTotalPayments = useSetAtom(totalPaymentsAtom)
  const { batchType } = queryString.parse(window.location.search) as { batchType: string }

  const fiatBatchInformation = useAtomValue(fiatBatchInformationAtom)
  const setWalletType = useSetAtom(walletTypeAtom)
  const [preProcessedRecordToUpdate, setPreProcessedRecordToUpdate] = useAtom<PreProcessedPayouts>(
    preProcessedRecordToUpdateAtom
  )
  const [cryptoRecordToUpdate, setCryptoRecordToUpdate] = useState<CryptoPayout>()
  const [isEditPayoutModalVisible, setIsEditPayoutModalVisible] = useState(false)
  const [isWalletSheetOpen, setIsWalletSheetOpen] = useState(false)
  const [isMassPayoutsSheetsOpen, setIsMassPayoutsSheetsOpen] = useState(false)
  const [isContactInstrumentSheetOpen, setIsContactInstrumentSheetOpen] = useState(false)
  const [activeFiatRecord, setActiveFiatRecord] = useState(null)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  const {
    page: paginationPage,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSimplePagination(10)

  const isCrypto = useMemo(() => {
    if (batchType) {
      setWalletType(batchType)
      return batchType?.toLowerCase() === WalletTypes.CRYPTO.toLowerCase()
    }
  }, [batchType])

  useEffect(() => {
    setIsEditPayoutModalVisible(false)
  }, [])

  const { data: cryptoCurrencies, isLoading: isCryptoCurrenciesLoading } =
    useCryptoCurrenciesQuery()

  const tableColumns = useMemo((): DataTableHeaderProps[] => {
    return [
      {
        key: 'amount',
        label: t('massPayouts.amount'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'recipient',
        label: isCrypto ? t('massPayouts.recipientAddressAndTag') : t('massPayouts.recipient'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'paymentReference',
        label: t('massPayouts.paymentReference'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'status',
        label: t('massPayouts.status'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ]
  }, [isCrypto])
  /* #region Fiat ----------------------------------------- */

  const isFiatQueryEnabled = !isCrypto && !!batchId

  const { mutate: mutateDeletePreProcessedRecord, isPending: isDeletingRecord } =
    useDeletePreProcessedRecordMutation()

  const { data: massFiatPayoutDetails, isLoading: loadingMassFiatPayoutDetails } =
    useGetMassFiatPayoutsDetailsQuery(batchId, isFiatQueryEnabled)

  const {
    data: { content: massFiatPayoutRecords, pagination: fiatDetailsPagination } = {},
    isFetching: loadingMassFiatPayoutRecords,
  } = useGetMassFiatPayoutsRecordsQuery({
    batchReference: batchId,
    page: paginationPage,
    size: rowsPerPage,
    enabled: isFiatQueryEnabled,
  })

  const { paging: fiatPaging, showPagination: showFiatPagination } =
    usePaginationPagingPersist(fiatDetailsPagination)

  const massFiatPayoutInformation = massFiatPayoutDetails || fiatBatchInformation

  useEffect(() => {
    setTotalPayments(getSumOfRecords(massFiatPayoutInformation?.payoutStatuses))
  }, [massFiatPayoutInformation])

  const { data: wallets, isLoading: isLoadingWallets } = useWalletsQueryV2({
    walletType: WalletTypes.FIAT.toLowerCase(),
    searchTerm: massFiatPayoutInformation?.currency,
    max: 500,
  })
  const selectedWallet = wallets?.data?.find(
    wallet => wallet?.lsid === massFiatPayoutDetails?.walletId
  )

  const isFiatPayoutSubmitted =
    massFiatPayoutInformation?.status === PaymentStatus.PROCESSING ||
    massFiatPayoutInformation?.status === PaymentStatus.COMPLETED
  const massPayoutPreProcessedPayouts: PreProcessedPayouts[] =
    (massFiatPayoutRecords as unknown as PreProcessedPayouts[]) ?? []

  const {
    isSidePanelOpen: isPayoutDetailsOpen,
    sidePanelData: payoutDetails,
    paging: pageMassPayout,
    setIsSidePanelOpen: setIsPayoutDetailsOpen,
    updateSidePanel: updatePayoutDetails,
  } = useSidePanel<PreProcessedPayouts>(massPayoutPreProcessedPayouts)

  const onDeletePreProcessedRecordRequest = (reference: string) => {
    setActiveFiatRecord(reference)
    const payload = {
      reference: reference,
    }
    mutateDeletePreProcessedRecord(payload, {
      onSuccess: () => {
        setIsEditPayoutModalVisible(false)
        queryClient.invalidateQueries({
          queryKey: ['massFiatPayoutsRecords'],
        })
        queryClient.invalidateQueries({
          queryKey: ['massFiatPayoutsDetails'],
        })
        toast({
          header: t('massPayouts.paymentRemoved'),
          variant: 'success',
        })
      },
      onSettled: () => {
        setActiveFiatRecord(null)
      },
      onError: error => {
        toast({
          header: t('oopsSomethingWentWrong'),
          variant: 'error',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
        const bvknErrorStatus = error?.status
        if (bvknErrorStatus === 400) {
          // Display callout with appropriate error response
        } else {
          // Display error message sent from the BE.
        }
      },
    })
  }

  const onBackHandler = () => {
    setIsEditPayoutModalVisible(false)
  }

  const onOpenPayoutDetails = (payoutIndex?: number) => {
    updatePayoutDetails(payoutIndex)
    setIsPayoutDetailsOpen(true)
  }

  const handleShowEditFiatPayoutModal = async (
    e: MouseEvent<HTMLButtonElement>,
    payout: PreProcessedPayouts
  ) => {
    e?.stopPropagation()
    setPreProcessedRecordToUpdate(payout)
    setIsEditPayoutModalVisible(true)
  }

  const handleDeleteFiatPayout = async (
    e: MouseEvent<HTMLButtonElement>,
    payout: PreProcessedPayouts
  ) => {
    e?.stopPropagation()
    onDeletePreProcessedRecordRequest(payout?.reference)
  }

  const fiatTable = useMemo(() => {
    return getFiatBatchTableData({
      massPayoutPreProcessedPayouts,
      isFiatPayoutSubmitted,
      handleShowEditFiatPayoutModal,
      onOpenPayoutDetails,
      handleDeleteFiatPayout,
      isDeleting: isDeletingRecord,
      activeRecord: activeFiatRecord,
      t,
      darkMode,
    })
  }, [massFiatPayoutInformation, massPayoutPreProcessedPayouts, darkMode])

  const fiatBatchPayoutInformation = {
    batchName: massFiatPayoutInformation?.alias,
    totalAmount: massFiatPayoutDetails?.paymentSummary?.totalAmount,
    totalAmountCurrency: massFiatPayoutDetails?.currency,
    totalFee: massFiatPayoutDetails?.paymentSummary?.totalFee,
    totalFeeCurrency: massFiatPayoutDetails?.currency,
    walletName: selectedWallet?.description,
    walletNameCurrency: selectedWallet?.currency?.code,
  }

  const fiatBatchSummaryInfo = {
    reference: massFiatPayoutInformation?.reference,
    status: massFiatPayoutInformation?.status,
    numberOfPayments: getSumOfRecords(massFiatPayoutInformation?.payoutStatuses).toString(),
    totalBatchAmount: `${roundNumberWithCommas(massFiatPayoutInformation?.paymentSummary?.totalAmount ?? 0)} ${
      massFiatPayoutInformation?.currency
    }`,
    totalBatchFees: `${roundNumberWithCommas(massFiatPayoutInformation?.paymentSummary?.totalFee)} ${
      massFiatPayoutInformation?.currency
    }`,
    totalBatchAmountWithFees: `${roundNumberWithCommas(
      massFiatPayoutInformation?.paymentSummary?.totalAmountWithFee
    )} ${massFiatPayoutInformation?.currency}`,
    numberOfErrors: massFiatPayoutInformation?.payoutStatuses?.INVALID ?? 0,
  }
  /* #endregion --------------------------------------------  */

  /* #region Crypto ------------------------------------------------- */

  const {
    data: cryptoBatchPayout,
    isLoading: isPendingCryptoMassPayout,
    refetch: refetchCryptoBatchPayout,
  } = useGetCryptoMassPayoutById(batchId, isCrypto && !!batchId)

  const updateCryptoPayout = updateCryptoPayoutSchema(t)

  const {
    control: controlCrypto,
    formState: formCryptoState,
    register: registerCrypto,
    setValue: setCryptoValue,
    trigger: triggerCrypto,
    clearErrors: clearCryptoErrors,
    setError: setCryptoError,
  } = useCryptoForm<UpdateCryptoPayoutFormPayload>({
    resolver: yupResolver(updateCryptoPayout),
  })

  const { triggerContactSearch } = useTriggerContactSearch({
    setIsEditPayoutModalVisible,
    setIsWalletSheetOpen,
    setIsContactInstrumentSheetOpen,
    setIsMassPayoutsSheetsOpen,
    cryptoRecordToUpdate,
    clearCryptoErrors: (input: string) =>
      clearCryptoErrors(input as keyof UpdateCryptoPayoutFormPayload),
    setIsContactSheetOpen,
  })

  const { errors: cryptoErrors } = formCryptoState

  const [cryptoAmount, currency, protocol, recipientAddress, tag, reference, network] =
    useCryptoWatch({
      control: controlCrypto,
      name: [
        'cryptoAmount',
        'currency',
        'protocol',
        'recipientAddress',
        'tag',
        'reference',
        'network',
      ],
    })

  useEffect(() => {
    if (cryptoRecordToUpdate) {
      setCryptoValue(
        'cryptoAmount',
        String(
          cryptoRecordToUpdate?.fundingWalletAmount ??
            cryptoRecordToUpdate?.paidCurrencyAmount ??
            ''
        )
      )
      setCryptoValue('currency', cryptoRecordToUpdate?.currency)
      setCryptoValue('protocol', cryptoRecordToUpdate?.protocol)
      setCryptoValue('recipientAddress', cryptoRecordToUpdate?.address)
      setCryptoValue('tag', cryptoRecordToUpdate?.tag)
      setCryptoValue('reference', cryptoRecordToUpdate?.reference)
      setCryptoValue('network', cryptoRecordToUpdate?.network)
      setCryptoValue('contactId', cryptoRecordToUpdate?.contactId as unknown as string)
    }
  }, [cryptoRecordToUpdate])

  const massCryptoPayoutEditRecordProps = {
    externalId: cryptoRecordToUpdate?.externalId,
    cryptoAmount,
    currency,
    protocol,
    recipientAddress,
    tag,
    reference,
    network,
  }

  const handleShowEditCryptoPayoutModal = async (
    e: MouseEvent<HTMLButtonElement>,
    payout: CryptoPayout
  ) => {
    e?.stopPropagation()
    setIsEditPayoutModalVisible(true)
    setCryptoRecordToUpdate(payout)
  }

  const { mutate: mutateUpdateCryptoRecord, isPending: isPendingUpdateCryptoRecord } =
    usePutUpdateMassCryptoPayoutMutation()

  const handleOnCryptoChange = (field: UpdateCryptoPayoutFormPayloadKeys, value: string) => {
    setCryptoValue(field, value)
  }

  const validateCryptoPayout = (field: string) => {
    clearCryptoErrors(field as keyof UpdateCryptoPayoutFormPayload)

    const payload = {
      batchId,
      externalId: cryptoRecordToUpdate?.externalId,
      fundingWalletAmount: cryptoRecordToUpdate?.fundingWalletAmount ? cryptoAmount : null,
      paidCurrencyAmount: cryptoRecordToUpdate?.paidCurrencyAmount ? cryptoAmount : null,
      currency: currency,
      protocol: protocol,
      network: network,
      address: recipientAddress,
      reference: reference,
      tag: tag || '',
      onlyValidate: true,
    }

    if (!!cryptoRecordToUpdate?.externalId) {
      mutateUpdateCryptoRecord(payload, {
        onSuccess: response => {
          clearCryptoErrors()
        },
        onError: (error: BaseErrorResponse) => {
          const errorData = error?.data as Record<string, unknown> | undefined
          const updatedProperties = Object.keys(payload).reduce(
            (updates, key) => {
              if (errorData && key in errorData) {
                updates[key] = errorData[key]
              }
              return updates
            },
            {} as Record<string, unknown>
          )

          if (errorData?.errorMessage) {
            updatedProperties.errorMessage = errorData.errorMessage
          }

          setCryptoRecordToUpdate({
            ...cryptoRecordToUpdate,
            ...updatedProperties,
          })
        },
      })
    }
  }

  useEffect(() => {
    validateCryptoPayout('currency')
  }, [currency])

  useEffect(() => {
    validateCryptoPayout('protocol')
  }, [protocol])

  const handleUpdateCryptoPayout = async () => {
    const isValidDetails = await triggerCrypto([
      'cryptoAmount',
      'currency',
      'recipientAddress',
      'tag',
      // Add network and protocol only if they exist
      ...(protocol ? (['protocol'] as const) : []),
      ...(network ? (['network'] as const) : []),
    ])

    if (isValidDetails) {
      clearCryptoErrors()
      const payload = {
        batchId,
        externalId: cryptoRecordToUpdate?.externalId,
        fundingWalletAmount: cryptoRecordToUpdate?.fundingWalletAmount ? cryptoAmount : null,
        paidCurrencyAmount: cryptoRecordToUpdate?.paidCurrencyAmount ? cryptoAmount : null,
        currency: currency,
        protocol: protocol,
        network: network,
        address: recipientAddress,
        reference: reference,
        tag: tag || '',
        ...(cryptoRecordToUpdate?.contactId && {
          contactId: cryptoRecordToUpdate?.contactId?.[0]?.contactId,
        }),
      }

      mutateUpdateCryptoRecord(payload, {
        onSuccess: () => {
          setIsEditPayoutModalVisible(false)
          queryClient.invalidateQueries({ queryKey: reactQueryKeys.massCryptoPayoutById(batchId) })
          setCryptoRecordToUpdate(null)
          toast({
            header: t('massPayouts.paymentUpdated'),
            variant: 'success',
          })
        },
        onError: (error: BaseErrorResponse) => {
          setIsEditPayoutModalVisible(false)
          queryClient.invalidateQueries({ queryKey: reactQueryKeys.massCryptoPayoutById(batchId) })
          setCryptoRecordToUpdate(null)
          if (error.status !== 400) {
            toast({
              header: t('oopsSomethingWentWrong'),
              variant: 'error',
            })
            logSentryError('Error: mutateUpdateCryptoRecord', error, {
              payload,
            })
          }
        },
      })
    }
  }

  const cryptoBatchPayoutInformation = {
    batchName: cryptoBatchPayout?.fileName,
    walletName: cryptoBatchPayout?.merchantName,
    walletNameCurrency: cryptoBatchPayout?.walletCurrency,
  }

  const isCryptoBatchSubmitted =
    cryptoBatchPayout?.status === BatchStatus.PROCESSING ||
    cryptoBatchPayout?.status === BatchStatus.COMPLETED ||
    cryptoBatchPayout?.status !== BatchStatus.DRAFT

  const { mutate: mutateDeleteCryptoRecord, isPending: isPendingDeleteCryptoRecord } =
    useDeleteCryptoRecordMutation()

  const handleDeleteCryptoPayout = (payout?: CryptoPayout) => {
    mutateDeleteCryptoRecord(
      { batchId: batchId, externalId: payout?.externalId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: reactQueryKeys.massCryptoPayoutById(batchId),
          })
          toast({
            header: t('massPayouts.paymentRemoved'),
            variant: 'success',
          })

          setIsEditPayoutModalVisible(false)
          queryClient.invalidateQueries({ queryKey: reactQueryKeys.massCryptoPayoutById(batchId) })
          setCryptoRecordToUpdate(null)
        },
        onError: error => {
          toast({
            header: t('oopsSomethingWentWrong'),
            variant: 'error',
            duration: DurationInMilliseconds.TEN_SECONDS,
          })
          logSentryError('Error: mutateDeleteCryptoRecord', error, {
            batchId,
            externalId: payout?.externalId,
          })
        },
      }
    )
  }

  const cryptoTable = useMemo(() => {
    return getCryptoBatchTableData({
      cryptoBatchPayout,
      handleDeleteCryptoPayout,
      handleShowEditCryptoPayoutModal,
      onOpenPayoutDetails,
      t,
      cryptoCurrencies,
      darkMode,
    })
  }, [cryptoBatchPayout, darkMode])

  const cryptoBatchSummaryInfo: BatchSummaryInformation = {
    status: cryptoBatchPayout?.status,
    numberOfPayments: String(
      Number(cryptoBatchPayout?.totalItems) - Number(cryptoBatchPayout?.totalProcessedItems)
    ),
    numberOfErrors: getSumOfRecordsWithErrors(cryptoBatchPayout),
    walletName: cryptoBatchPayout?.walletName ?? cryptoBatchPayout?.merchantName,
    currency: cryptoBatchPayout?.walletCurrency,
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      isCrypto && refetchCryptoBatchPayout()
    }, 5000)

    return () => clearTimeout(timer)
  }, [refetchCryptoBatchPayout])

  /* #endregion Crypto ---------------------------------------------- */

  const isPayoutSubmitted = isCrypto ? isCryptoBatchSubmitted : isFiatPayoutSubmitted

  const numberOfErrors = isCrypto
    ? getSumOfRecordsWithErrors(cryptoBatchPayout)
    : getSumOfFiatRecordsWithErrors(massFiatPayoutInformation)
  const numberOfProcessedPayments = isCrypto
    ? cryptoBatchPayout?.totalProcessedItems
    : getValidRecords(massFiatPayoutInformation?.payoutStatuses)
  const numberOfPayments = isCrypto
    ? cryptoBatchPayout?.totalItems
    : getSumOfRecords(massFiatPayoutInformation?.payoutStatuses)

  const paymentsReadyToBeProcessed = isCrypto
    ? t('massPayouts.paymentsHaveBeenProcessedAndCanBeTracked', {
        numberOfProcessedPayments: numberOfProcessedPayments,
        numberOfPayments: numberOfPayments,
      })
    : t('massPayouts.paymentsReadyToBeProcessed', {
        numberOfProcessedPayments: numberOfProcessedPayments,
        numberOfPayments: numberOfPayments,
      })

  const isPayoutUpdateFormDisabled = () => {
    if (isCrypto) {
      return (
        !cryptoAmount ||
        !currency ||
        !protocol ||
        !recipientAddress ||
        !reference ||
        Object.keys(formCryptoState?.errors)?.length > 0
      )
    }

    return false
  }

  const onCloseEditModal = () => {
    setIsEditPayoutModalVisible(false)
    clearCryptoErrors()
  }

  const handleOnUpdateCrypto = () => {
    if (MER_CONTACTS_ERROR_CODES.includes(cryptoErrors?.travelRule?.type)) triggerContactSearch()
    else handleUpdateCryptoPayout()
  }
   
  return (
    <Box className="w-full">
      <MassPayoutHeader
        batchInformation={isCrypto ? cryptoBatchSummaryInfo : fiatBatchSummaryInfo}
        buttonGroup={MassPayoutButtonGroup.BATCH_DETAILS}
      />
      <Box className="mb-4" flex>
        <MassPayoutBatchInformation
          loading={loadingMassFiatPayoutDetails || isPendingCryptoMassPayout || isLoadingWallets}
          batchInformation={isCrypto ? cryptoBatchPayoutInformation : fiatBatchPayoutInformation}
        />
      </Box>

      <MassPayoutReviewCallouts
        loadingMassFiatPayoutDetails={loadingMassFiatPayoutRecords}
        cryptoBatchPayout={cryptoBatchPayout}
        massFiatPayoutInformation={massFiatPayoutInformation}
        massPayoutPreProcessedPayouts={massPayoutPreProcessedPayouts}
        isPendingCryptoMassPayout={isPendingCryptoMassPayout}
        isPendingDeleteCryptoRecord={isPendingDeleteCryptoRecord}
        isCrypto={isCrypto}
        paymentsReadyToBeProcessed={paymentsReadyToBeProcessed}
        isPayoutSubmitted={isPayoutSubmitted}
        numberOfErrors={numberOfErrors}
      />

      {(!!massPayoutPreProcessedPayouts?.length ||
        !!cryptoBatchPayout?.items?.length ||
        !isCryptoCurrenciesLoading) && (
        <DataTable
          name="mass-payouts-batch-table"
          headers={tableColumns}
          data={isCrypto ? cryptoTable : fiatTable}
        />
      )}
      {!!fiatPaging && !showFiatPagination && (
        <Box
          className={`${loadingMassFiatPayoutRecords || showFiatPagination ? 'opacity-25' : ''}`}
        >
          <Pagination
            testId="mass-payouts-fiat-pagination"
            page={paginationPage}
            rowsPerPage={rowsPerPage}
            total={fiatPaging?.total ?? 0}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      )}
      {!isCrypto && (
        <MassPayoutSidePanel
          massPayout={massFiatPayoutDetails}
          isOpen={isPayoutDetailsOpen}
          preProcessedPayout={payoutDetails}
          pageMassPayout={pageMassPayout}
          onClose={() => setIsPayoutDetailsOpen(false)}
          isLoadingMassFiatPayoutDetail={loadingMassFiatPayoutDetails}
        />
      )}
      <MassPayoutModal />

      <MassPayoutEditRecordContainer
        key={
          isCrypto
            ? massCryptoPayoutEditRecordProps?.externalId
            : preProcessedRecordToUpdate?.reference
        }
        isEditPayoutModalVisible={isEditPayoutModalVisible}
        onBackHandler={onBackHandler}
        onCloseModal={onCloseEditModal}
        errors={formCryptoState.errors}
      >
        {isCrypto ? (
           <CryptoPayoutEditRecordForm
            massPayoutEditRecordProps={massCryptoPayoutEditRecordProps}
            errors={formCryptoState.errors}
            handleOnChange={handleOnCryptoChange}
            register={registerCrypto}
            setError={setCryptoError}
            payout={cryptoRecordToUpdate}
            walletName={cryptoBatchPayout?.walletName}
            walletCurrency={cryptoBatchPayout?.walletCurrency}
            validateCryptoPayout={validateCryptoPayout}
            clearErrors={clearCryptoErrors}
            cryptoCurrencies={cryptoCurrencies}
            isFormDisabled={isPayoutUpdateFormDisabled()}
            travelRuleError={formCryptoState.errors?.travelRule}
            onCloseModal={onCloseEditModal}
            onDelete={() => handleDeleteCryptoPayout(cryptoRecordToUpdate)}
            onUpdate={handleOnUpdateCrypto}
            isLoadingDelete={isPendingDeleteCryptoRecord}
            isLoadingUpdate={isPendingUpdateCryptoRecord}
          />
        ) : (
         <FiatPayoutEditRecordForm
            record={preProcessedRecordToUpdate}
            setIsEditPayoutModalVisible={setIsEditPayoutModalVisible}
            setPreProcessedRecordToUpdate={setPreProcessedRecordToUpdate}
            walletCurrency={selectedWallet?.currency?.code}
            isDeletingPreProcessedRecord={isDeletingRecord}
            onDeletePreProcessedRecordRequest={onDeletePreProcessedRecordRequest}
          />
        )}
      </MassPayoutEditRecordContainer>
      <WalletSheet isSheetOpen={isWalletSheetOpen} />
      <ContactInstrumentsSheet isSheetOpen={isContactInstrumentSheetOpen} isNestedSheet />
      <MassPayoutsSheets isSheetOpen={isMassPayoutsSheetsOpen} />
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet={true} />
    </Box>
  )
}
